import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import ThemeToggle from "@/components/ThemeToggle";
import { useLiquidGlass } from "@/contexts/LiquidGlassContext";
import { cn } from "@/lib/utils";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const { liquidGlassEnabled, shimmerDirection } = useLiquidGlass();

  const links = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services & Experiences" },
    { to: "/book", label: "Book Now" },
  ];

  return (
    <nav className={cn(
      "fixed top-0 left-0 right-0 z-50 border-b border-border transition-all duration-500",
      liquidGlassEnabled ? `liquid-glass shimmer-${shimmerDirection}` : "bg-background/80 backdrop-blur-lg"
    )}>
      <div className="container mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <span className="font-serif text-2xl md:text-3xl font-bold tracking-wider text-foreground italic">
            Paradise <span className="text-primary not-italic">Beauty</span>
          </span>
        </Link>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-6">
          <ThemeToggle />
          {links.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-sm font-medium tracking-widest uppercase transition-colors hover:text-primary ${
                location.pathname === link.to ? "text-primary" : "text-muted-foreground"
              }`}
            >
              {link.label}
            </Link>
          ))}
          {user ? (
            <Button asChild variant="outline" size="sm" className="rounded-full px-6 font-semibold tracking-wider uppercase text-xs border-primary/30 hover:bg-primary/10">
              <Link to="/account"><User size={14} className="mr-1" /> Account</Link>
            </Button>
          ) : (
            <Button size="sm" className="rounded-full px-6 font-semibold tracking-wider uppercase text-xs" onClick={() => { window.location.href = "/auth"; }}>
              Sign In
            </Button>
          )}
        </div>

        {/* Mobile: toggle + hamburger */}
        <div className="flex md:hidden items-center gap-3">
          <ThemeToggle />
          <button className="text-foreground" onClick={() => setIsOpen(!isOpen)} aria-label="Toggle menu">
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className={cn(
              "md:hidden border-b border-border overflow-hidden",
              liquidGlassEnabled ? `liquid-glass shimmer-${shimmerDirection}` : "bg-background"
            )}
          >
            <div className="flex flex-col items-center gap-6 py-8">
              {links.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setIsOpen(false)}
                  className={`text-sm font-medium tracking-widest uppercase transition-colors hover:text-primary ${
                    location.pathname === link.to ? "text-primary" : "text-muted-foreground"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              {user ? (
                <Button asChild variant="outline" size="sm" className="rounded-full px-6 font-semibold tracking-wider uppercase text-xs border-primary/30 hover:bg-primary/10">
                  <Link to="/account" onClick={() => setIsOpen(false)}><User size={14} className="mr-1" /> Account</Link>
                </Button>
              ) : (
                <Button size="sm" className="rounded-full px-6 font-semibold tracking-wider uppercase text-xs" onClick={() => { setIsOpen(false); window.location.href = "/auth"; }}>
                  Sign In
                </Button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
